import { ChangeLog, Response, apiAbortController } from '@ui/src';
import {
  EditNotificationsValues,
  NotificationItem,
  NotificationItemComment,
} from '@notifications/hoc/center/types';
import api from '../../../api/api';

export const reportsNotificationCenterComponentApi = api.injectEndpoints({
  endpoints: (build) => ({
    getReportsNotificationsList: build.query<
      Response<NotificationItem>,
      string
    >({
      query: (query) => ({
        url: `/v1/notifications/reports${query}${
          query ? '&' : '?'
        }take=20&sortProp=createdAt&sortDir=desc`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getReportsNotificationItem: build.query<{ data: NotificationItem }, string>(
      {
        query: (id) => ({
          url: `/v1/notifications/reports/${id}`,
          signal: apiAbortController.getSignalToken(),
        }),
      },
    ),
    getReportsNotificationItemComment: build.query<
      Response<NotificationItemComment>,
      string
    >({
      query: (id) => ({
        url: `/v1/notifications/reports/${id}/comments?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addReportsNotificationItemComment: build.mutation<
      { data: NotificationItemComment },
      { id: string; body: { content: string } }
    >({
      query: (data) => ({
        url: `/v1/notifications/reports/${data.id}/comments`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateReportsNotificationIssue: build.mutation<
      { data: NotificationItem },
      {
        id: string;
        body: {
          responsibleId?: string;
          executorId?: string;
          status?: string;
          deadline?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/notifications/reports/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getReportsNotificationChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchUpdateReportsNotificationIssue: build.mutation<
      { data: number },
      EditNotificationsValues
    >({
      query: (data) => ({
        url: '/v1/notifications/reports/batch-update',
        method: 'PUT',
        body: data,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getReportsNotificationEmployees: build.query<
      Response<{
        id: string;
        profile: {
          firstName: string;
          lastName: string;
        };
      }>,
      void
    >({
      query: () => ({
        url: `/v1/employees?take=9999`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchAddReportsNotificationItemComment: build.mutation<
      { data: number },
      { ids: string[]; content: string }
    >({
      query: (data) => ({
        url: '/v1/notifications/reports/batch-comments',
        method: 'POST',
        body: data,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateReportsNotificationIssueComment: build.mutation<
      { data: NotificationItemComment },
      {
        id: string;
        commentId: string;
        body: {
          content: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/notifications/reports/${data.id}/comments/${data.commentId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetReportsNotificationsListQuery,
  useLazyGetReportsNotificationItemCommentQuery,
  useAddReportsNotificationItemCommentMutation,
  useUpdateReportsNotificationIssueMutation,
  useLazyGetReportsNotificationChangeLogsQuery,
  useBatchAddReportsNotificationItemCommentMutation,
  useBatchUpdateReportsNotificationIssueMutation,
  useLazyGetReportsNotificationItemQuery,
  useUpdateReportsNotificationIssueCommentMutation,
  useLazyGetReportsNotificationEmployeesQuery,
} = reportsNotificationCenterComponentApi;

export const {
  endpoints: {
    getReportsNotificationsList,
    addReportsNotificationItemComment,
    getReportsNotificationItemComment,
    updateReportsNotificationIssue,
    getReportsNotificationChangeLogs,
    updateReportsNotificationIssueComment,
    batchAddReportsNotificationItemComment,
    batchUpdateReportsNotificationIssue,
    getReportsNotificationEmployees,
    getReportsNotificationItem,
  },
} = reportsNotificationCenterComponentApi;
